import React, { useState } from 'react';
import { Icon } from '@iconify/react';
import { softDeleteAccount } from '../../../services/userService';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

function DeleteAccount() {
  const [showModal, setShowModal] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [reason, setReason] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);

  const handleOpenModal = () => setShowModal(true);
  const handleCloseModal = () => {
    setShowModal(false);
    setConfirmText('');
    setReason('');
  };

  const handleReasonChange = (e) => setReason(e.target.value);
  const handleConfirmTextChange = (e) => setConfirmText(e.target.value);

  const handleDeleteAccount = async () => {
    setIsDeleting(true);
  
    // Simulate 2 seconds loading before making API call
    setTimeout(async () => {
      try {
        const response = await softDeleteAccount();
        if (response.success) {
          toast.success('Account deleted successfully.');
          handleCloseModal();
          // Optional: redirect after deletion
          // setTimeout(() => window.location.href = '/login', 3000);
        } else {
          toast.error(response.error_msg || 'Something went wrong.');
        }
      } catch (error) {
        console.error('Error deleting account:', error);
        toast.error('Failed to delete account.');
      } finally {
        setIsDeleting(false);
      }
    }, 2000); // ⏳ 2 second simulated delay
  };
  

  const isDeleteButtonDisabled = confirmText !== 'DELETE';

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-[var(--text-primary)] mb-1">Delete Account</h2>
        <p className="text-sm text-[var(--text-secondary)]">Permanently delete your account and all associated data.</p>
      </div>

      <div className="rounded-xl border border-amber-200 bg-amber-50 dark:bg-amber-950/20 p-5 mb-5 flex gap-4">
        <div className="flex-shrink-0">
          <Icon icon="mdi:alert-circle-outline" width="24" height="24" className="text-yellow-600" />
        </div>
        <div className="flex-1">
          <h4 className="font-semibold text-yellow-900 mb-2">Warning: This action cannot be undone</h4>
          <p className="text-yellow-800 mb-2">
            Deleting your account will permanently remove all your data, including:
          </p>
          <ul className="list-disc list-inside text-yellow-800 mb-2 space-y-1">
            <li>Course progress and completion records</li>
            <li>Certificates and achievements</li>
            <li>Personal information and profile data</li>
            <li>Payment history and subscription details</li>
          </ul>
          <p className="text-yellow-800">
            If you're experiencing issues with the platform, please consider
            <a href="#" className="ml-1 text-yellow-700 underline">contacting support</a> before deleting your account.
          </p>
        </div>
      </div>

      <div>
        <button
          type="button"
          className="bg-red-500 hover:bg-red-600 text-white font-medium py-2.5 px-5 rounded-xl flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          onClick={handleOpenModal}
          disabled={true}
        >
          <Icon icon="mdi:delete-outline" width="18" height="18" />
          Coming Soon
        </button>
      </div>

      {/* Confirmation Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-[var(--bg-primary)] rounded-2xl shadow-xl max-w-lg w-full overflow-hidden">
            <div className="px-5 py-4 border-b border-[var(--border-color)] flex justify-between items-center">
              <h3 className="text-lg font-semibold text-red-500 flex items-center gap-2">
                <Icon icon="mdi:alert-circle-outline" width="24" height="24" />
                Confirm Account Deletion
              </h3>
              <button
                type="button"
                onClick={handleCloseModal}
                className="p-2 rounded-xl text-[var(--text-secondary)] hover:bg-[var(--bg-secondary)] hover:text-[var(--text-primary)]"
              >
                <Icon icon="mdi:close" width="24" height="24" />
              </button>
            </div>
            <div className="p-5">
              <p className="mb-4 text-gray-700">
                We're sorry to see you go. Before you proceed, please help us understand why you're leaving:
              </p>

              <div className="mb-3">
                <label className="block text-sm font-medium text-gray-700 mb-1">Reason for leaving (optional)</label>
                <select
                  className="w-full px-3 py-2 border border-input-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20"
                  value={reason}
                  onChange={handleReasonChange}
                >
                  <option value="">Select a reason</option>
                  <option value="not_useful">I don't find the platform useful</option>
                  <option value="too_expensive">The service is too expensive</option>
                  <option value="difficult_to_use">The platform is difficult to use</option>
                  <option value="found_alternative">I found a better alternative</option>
                  <option value="privacy_concerns">I have privacy concerns</option>
                  <option value="other">Other reason</option>
                </select>
              </div>

              <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-3">
                <p className="mb-2 font-semibold text-red-900"><strong>This action cannot be undone.</strong></p>
                <p className="text-red-800">All your data will be permanently deleted and cannot be recovered.</p>
              </div>

              <div className="mb-3">
                <label className="block text-sm font-medium text-gray-700 mb-1">Type DELETE to confirm</label>
                <input
                  type="text"
                  className="w-full px-3 py-2 border border-input-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20"
                  value={confirmText}
                  onChange={handleConfirmTextChange}
                  placeholder="DELETE"
                />
              </div>
            </div>
            <div className="px-5 py-4 border-t border-[var(--border-color)] flex justify-end gap-3">
              <button type="button" className="px-4 py-2.5 rounded-xl font-medium text-[var(--text-secondary)] hover:bg-[var(--bg-secondary)]" onClick={handleCloseModal}>
                Cancel
              </button>
              <button
                type="button"
                className="bg-red-500 hover:bg-red-600 text-white font-medium py-2.5 px-5 rounded-xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                onClick={handleDeleteAccount}
                disabled={isDeleteButtonDisabled || isDeleting}
              >
                {isDeleting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Deleting...
                  </>
                ) : (
                  'Permanently Delete Account'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default DeleteAccount;
